import { z } from 'zod';
import { listingValues, numberParameter, switchParameter, unchangedFrom, overrideZoom, overrideZoomParameter } from './shared.js';

const transmissionRowValues = {
  zoom: overrideZoom,
  rays_across_diameter: z.number().int().nullable().optional(),
  full_listing: z.enum(listingValues).optional(),
};
const transmissionRow = z.union([
  z.strictObject({ ...transmissionRowValues, rays_across_diameter: z.number().int() }),
  z.strictObject({ ...transmissionRowValues, full_listing: z.enum(listingValues) }),
  z.strictObject({ ...transmissionRowValues, list_polarization: z.enum(listingValues),
    polarization: z.enum(['GEO', 'STP']).optional(),
  }),
]);
const transmissionSettings = z.strictObject({
  option_settings: z.array(transmissionRow).default([]).describe('Ordered TRA overrides. Empty retains native NRD 20, FUL No and PLI No. FUL includes surface losses for every field; PLI includes averaged polarization in GEO or STP form. With polarization listing enabled, an omitted representation uses native GEO, including after an earlier Stokes setting.'),
});
export default {
  label: 'Transmission', group: 'performance',
  description: 'Surface losses, total transmittance and relative illumination, with optional pupil-averaged polarization.',
  settingsSchema: transmissionSettings,
  parameters: [
    { key: 'option_settings', label: 'Options', kind: 'rows', columns: [overrideZoomParameter,
      { ...numberParameter('rays_across_diameter', 'Rays across diameter', { integer: true, blank: 'Unchanged' }), omit: unchangedFrom('20') },
      { ...switchParameter('full_listing', 'Surface losses at every field'), omit: unchangedFrom('No') },
      { ...switchParameter('list_polarization', 'Polarization listing'), omit: unchangedFrom('No') },
      // Not 'Unchanged': a PLI without a representation selects GEO, even after an earlier STP row.
      { key: 'polarization', label: 'Polarization representation', kind: 'select',
        omit: { first: 'Geometrical (default)', later: 'Geometrical (default)' },
        when: { list_polarization: ['Yes', 'No'] }, options: [
          { value: 'GEO', label: 'Geometrical' }, { value: 'STP', label: 'Stokes parameters' },
        ],
      },
    ] },
  ],
  coverage: 'Every field, for each wavelength and in total. Relative illumination is referenced to the first field and assumes a unit-radiance source. Surfaces with no coating use CODE V\'s default single-layer quarter-wave MgF2 on air-glass surfaces, not bare glass; coatings set in your design are not carried over.',
} as const;
